import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { BrandGradient } from '../components/common/PFILogo';
import { formatProjectName } from '../utils/projectUtils';
import { formatDate } from '../utils/dateFormatters';
import { 
  getSupplierPerformanceMetrics, 
  getSimilarProjectPerformance,
  getPerformanceBadge 
} from '../lib/supplierPerformance';
import { 
  ArrowLeftIcon,
  ScaleIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  ClockIcon,
  StarIcon,
  TrophyIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

export default function QuoteComparisonPage() {
  const { id, taskId } = useParams();
  const [project, setProject] = useState(null);
  const [task, setTask] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [supplierPerformance, setSupplierPerformance] = useState({});
  const [aiAnalyses, setAiAnalyses] = useState({});
  const [loadingAnalysis, setLoadingAnalysis] = useState({});
  const [acceptingQuote, setAcceptingQuote] = useState(null);

  useEffect(() => {
    loadData();
  }, [id, taskId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectDoc = await getDoc(doc(db, 'projects', id));
      if (projectDoc.exists()) {
        setProject({ id: projectDoc.id, ...projectDoc.data() });
      }

      // Load task
      const taskDoc = await getDoc(doc(db, 'projects', id, 'tasks', taskId));
      if (taskDoc.exists()) {
        setTask({ id: taskDoc.id, ...taskDoc.data() });
      }

      // Load submitted quotes only
      const quotesSnapshot = await getDocs(collection(db, 'projects', id, 'tasks', taskId, 'quotes'));
      const quotesData = quotesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(quote => quote.status === 'submitted' && quote.amount);
      
      // Load supplier performance data for each quote
      const performanceData = {};
      for (const quote of quotesData) {
        if (quote.supplierId) {
          try {
            const performance = await getSupplierPerformanceMetrics(
              quote.supplierId, 
              taskDoc.exists() ? taskDoc.data().category : null
            );
            const similarWork = await getSimilarProjectPerformance(
              quote.supplierId,
              null,
              taskDoc.exists() ? taskDoc.data().category : null
            );
            performanceData[quote.supplierId] = {
              ...performance,
              similarWork
            };
          } catch (error) {
            console.error(`Error loading performance for supplier ${quote.supplierId}:`, error);
            performanceData[quote.supplierId] = {
              hasHistory: false,
              averageRating: 0,
              projectCount: 0
            };
          }
        }
      }
      
      setSupplierPerformance(performanceData);
      setQuotes(quotesData);
      
      // Auto-select all submitted quotes for comparison
      setSelectedQuotes(quotesData.map(q => q.id));
      
      // Load AI analyses for each quote
      if (quotesData.length > 0) {
        loadAIAnalyses(quotesData, taskDoc.data());
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAIAnalyses = async (quotesData, taskData) => {
    // Get all supplier history first
    const supplierIds = [...new Set(quotesData.map(q => q.supplierId))];
    const supplierHistories = {};

    for (const supplierId of supplierIds) {
      try {
        // Get historical quotes for this supplier
        const allProjectsSnapshot = await getDocs(collection(db, 'projects'));
        const history = [];
        
        for (const projectDoc of allProjectsSnapshot.docs) {
          const project = projectDoc.data();
          const tasksSnapshot = await getDocs(collection(db, 'projects', projectDoc.id, 'tasks'));
          
          for (const taskDoc of tasksSnapshot.docs) {
            const quotesSnapshot = await getDocs(collection(db, 'projects', projectDoc.id, 'tasks', taskDoc.id, 'quotes'));
            
            for (const quoteDoc of quotesSnapshot.docs) {
              const quote = quoteDoc.data();
              if (quote.supplierId === supplierId) {
                history.push({
                  projectName: project.name,
                  taskName: taskDoc.data().name,
                  amount: quote.amount,
                  status: quote.status,
                  description: quote.description,
                  submittedAt: quote.submittedAt
                });
              }
            }
          }
        }
        
        supplierHistories[supplierId] = history;
      } catch (error) {
        console.error(`Error loading history for supplier ${supplierId}:`, error);
        supplierHistories[supplierId] = [];
      }
    }

    // Now analyze each quote
    for (const quote of quotesData) {
      try {
        setLoadingAnalysis(prev => ({ ...prev, [quote.id]: true }));
        
        const analyzeQuote = httpsCallable(functions, 'analyzeQuoteAndSupplier');
        const result = await analyzeQuote({
          quote: {
            ...quote,
            supplierName: quote.supplierName,
            amount: quote.amount,
            description: quote.description,
            estimatedCompletionDate: quote.estimatedCompletionDate,
            timelineDays: quote.timelineDays,
            timeline: quote.timeline
          },
          task: {
            name: taskData.name,
            description: taskData.description,
            notes: taskData.notes,
            projectName: project?.name
          },
          supplierHistory: supplierHistories[quote.supplierId] || []
        });

        setAiAnalyses(prev => ({
          ...prev,
          [quote.id]: result.data
        }));

      } catch (error) {
        console.error(`Error analyzing quote ${quote.id}:`, error);
        setAiAnalyses(prev => ({
          ...prev,
          [quote.id]: {
            analysis: 'AI analysis failed to load. Please review the quote manually.',
            hasAnalysis: false,
            error: true
          }
        }));
      } finally {
        setLoadingAnalysis(prev => ({ ...prev, [quote.id]: false }));
      }
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      setAcceptingQuote(quoteId);
      
      // Find the accepted quote details
      const acceptedQuote = quotes.find(q => q.id === quoteId);
      if (!acceptedQuote) {
        alert('Error: Quote not found');
        setAcceptingQuote(null);
        return;
      }

      // Update the accepted quote
      await updateDoc(doc(db, 'projects', id, 'tasks', taskId, 'quotes', quoteId), {
        status: 'accepted',
        acceptedAt: new Date()
      });
      
      // Update the task with the accepted supplier information
      // Handle both old (timeline/timelineDays) and new (estimatedCompletionDate) formats
      const taskUpdate = {
        status: 'quote_accepted',
        acceptedQuoteId: quoteId,
        acceptedSupplierId: acceptedQuote.supplierId,
        acceptedSupplierName: acceptedQuote.supplierName,
        acceptedSupplierEmail: acceptedQuote.supplierEmail,
        acceptedAmount: acceptedQuote.amount,
        quoteAcceptedAt: new Date(),
        updatedAt: new Date()
      };
      
      // Add completion date or timeline based on what's available
      if (acceptedQuote.estimatedCompletionDate) {
        taskUpdate.acceptedCompletionDate = acceptedQuote.estimatedCompletionDate;
      } else if (acceptedQuote.timeline) {
        taskUpdate.acceptedTimeline = acceptedQuote.timeline;
      }
      
      if (acceptedQuote.timelineDays) {
        taskUpdate.acceptedTimelineDays = acceptedQuote.timelineDays;
      }
      
      await updateDoc(doc(db, 'projects', id, 'tasks', taskId), taskUpdate);
      
      // Mark other quotes as declined
      const otherQuotes = quotes.filter(q => q.id !== quoteId);
      for (const quote of otherQuotes) {
        await updateDoc(doc(db, 'projects', id, 'tasks', taskId, 'quotes', quote.id), {
          status: 'declined',
          declinedAt: new Date()
        });
      }

      // Send notifications to the accepted supplier
      try {
        // Format completion info for notifications
        let completionInfo = 'Not specified';
        if (acceptedQuote.estimatedCompletionDate) {
          completionInfo = `Completion Date: ${formatDate(acceptedQuote.estimatedCompletionDate)}`;
        } else if (acceptedQuote.timelineDays) {
          completionInfo = `Timeline: ${acceptedQuote.timelineDays} days`;
        } else if (acceptedQuote.timeline) {
          completionInfo = `Timeline: ${acceptedQuote.timeline}`;
        }

        const notificationData = {
          projectName: project.name,
          taskName: task.name,
          supplierName: acceptedQuote.supplierName,
          supplierEmail: acceptedQuote.supplierEmail,
          quoteAmount: formatCurrency(acceptedQuote.amount),
          completionInfo,
          customerName: project.customerName,
          customerPhone: project.customerPhone,
          customerEmail: project.customerEmail
        };

        // Send email notification
        const sendEmailNotification = httpsCallable(functions, 'sendQuoteAcceptanceNotification');
        await sendEmailNotification(notificationData);

        // Send SMS notification if phone number is available
        if (acceptedQuote.supplierPhone) {
          const sendSMSNotification = httpsCallable(functions, 'sendQuoteAcceptanceSMS');
          await sendSMSNotification({
            supplierPhone: acceptedQuote.supplierPhone,
            supplierName: acceptedQuote.supplierName,
            projectName: project.name,
            taskName: task.name,
            quoteAmount: formatCurrency(acceptedQuote.amount)
          });
        }

        console.log('Notifications sent successfully');
      } catch (notificationError) {
        console.error('Error sending notifications:', notificationError);
        // Don't fail the quote acceptance if notifications fail
      }
      
      // Format timeline/date for display
      let completionInfo = 'Not specified';
      if (acceptedQuote.estimatedCompletionDate) {
        completionInfo = `Completion Date: ${formatDate(acceptedQuote.estimatedCompletionDate)}`;
      } else if (acceptedQuote.timelineDays) {
        completionInfo = `Timeline: ${acceptedQuote.timelineDays} days`;
      } else if (acceptedQuote.timeline) {
        completionInfo = `Timeline: ${acceptedQuote.timeline}`;
      }
      
      alert(`✅ Quote accepted!\n\nSupplier: ${acceptedQuote.supplierName}\nAmount: ${formatCurrency(acceptedQuote.amount)}\n${completionInfo}\n\nThe supplier has been notified via email${acceptedQuote.supplierPhone ? ' and SMS' : ''}.`);
      
      loadData();
    } catch (error) {
      console.error('Error accepting quote:', error);
      alert('Failed to accept quote: ' + error.message);
    } finally {
      setAcceptingQuote(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };


  const formatTimeline = (quote) => {
    // Handle new estimatedCompletionDate field
    if (quote.estimatedCompletionDate) {
      return formatDate(quote.estimatedCompletionDate);
    }
    // Handle old timelineDays field
    if (quote.timelineDays) {
      if (quote.timelineDays === 1) return '1 day';
      if (quote.timelineDays <= 7) return `${quote.timelineDays} days`;
      if (quote.timelineDays <= 30) return `${quote.timelineDays} days (${Math.ceil(quote.timelineDays / 7)} weeks)`;
      return `${quote.timelineDays} days (${Math.ceil(quote.timelineDays / 30)} months)`;
    }
    // Handle old timeline text field
    return quote.timeline || 'Not specified';
  };

  const calculateScore = (quote) => {
    let score = 0;
    
    // Price score (lower is better, max 30 points - reduced to make room for AI quality)
    const prices = quotes.map(q => q.amount);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (maxPrice > minPrice) {
      score += ((maxPrice - quote.amount) / (maxPrice - minPrice)) * 30;
    } else {
      score += 30;
    }
    
    // Timeline score (max 15 points - reduced to make room for AI quality)
    if (quote.timelineDays) {
      const taskCategory = task?.category?.toLowerCase();
      
      if (taskCategory === 'upholstery') {
        // Upholstery timeline scale (months-based)
        if (quote.timelineDays <= 30) score += 15;      // 1 month or less - Excellent
        else if (quote.timelineDays <= 60) score += 13;  // 2 months - Very Good
        else if (quote.timelineDays <= 90) score += 11;  // 3 months - Good
        else if (quote.timelineDays <= 120) score += 9;  // 4 months - Acceptable
        else if (quote.timelineDays <= 150) score += 6;  // 5 months - Slow
        else if (quote.timelineDays <= 180) score += 4;  // 6 months - Very Slow
        else score += 2; // Over 6 months
      } else {
        // Default/Lighting timeline scale (days/weeks-based)
        if (quote.timelineDays <= 7) score += 15;       // 1 week - Excellent
        else if (quote.timelineDays <= 14) score += 13;  // 2 weeks - Very Good
        else if (quote.timelineDays <= 21) score += 11;  // 3 weeks - Good
        else if (quote.timelineDays <= 30) score += 9;   // 1 month - Acceptable
        else if (quote.timelineDays <= 45) score += 6;   // 6 weeks - Slow
        else score += 4; // Over 6 weeks
      }
    } else if (quote.timeline) {
      score += 7; // Fallback for old text timeline
    }

    // AI Quality Assessment (25 points max - most important for quote quality)
    const aiAnalysis = aiAnalyses[quote.id];
    if (aiAnalysis && aiAnalysis.qualityScore) {
      // AI quality score is 0-100, convert to 0-25 points
      score += (aiAnalysis.qualityScore / 100) * 25;
    } else {
      // If no AI analysis, give neutral score of 12.5 points (50% of max)
      score += 12.5;
    }

    // Historical performance score (20 points max - reduced to make room for AI quality)
    const performance = supplierPerformance[quote.supplierId];
    if (performance && performance.hasHistory) {
      const performanceMultiplier = performance.averageRating / 5; // 0-1 multiplier
      score += 20 * performanceMultiplier;
      
      // Bonus for similar work experience
      if (performance.similarWork && performance.similarWork.hasSimilarWork) {
        score += 2; // Bonus for relevant experience
      }
      
      // Trend bonus/penalty
      if (performance.recentTrend === 'improving') score += 1.5;
      else if (performance.recentTrend === 'declining') score -= 1.5;
    } else {
      // New supplier gets middle score
      score += 10;
    }

    // Quote completeness and quality (10 points max - reduced as AI now handles quality assessment)
    if (quote.description && quote.description.length > 50) score += 3;
    if (quote.materials && quote.materials.length > 20) score += 3;
    if (quote.notes && quote.notes.length > 10) score += 2;
    if (quote.timelineDays > 0) score += 2; // Provided specific timeline
    
    return Math.min(Math.round(score), 100); // Cap at 100
  };

  const getBestValue = () => {
    if (quotes.length === 0) return null;
    const quotesWithScores = quotes.map(q => ({ ...q, score: calculateScore(q) }));
    return quotesWithScores.reduce((best, current) => 
      current.score > best.score ? current : best
    );
  };

  const getLowestPrice = () => {
    if (quotes.length === 0) return null;
    return quotes.reduce((lowest, current) => 
      current.amount < lowest.amount ? current : lowest
    );
  };

  const getMostDetailedQuote = () => {
    if (quotes.length === 0) return null;
    // Return quote with most detail (has timeline and description)
    return quotes.reduce((most, current) => {
      const currentScore = (current.timeline ? 1 : 0) + (current.description ? 1 : 0) + (current.materials ? 1 : 0);
      const mostScore = (most.timeline ? 1 : 0) + (most.description ? 1 : 0) + (most.materials ? 1 : 0);
      return currentScore > mostScore ? current : most;
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading quotes for comparison...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const bestValue = getBestValue();
  const lowestPrice = getLowestPrice();
  const mostDetailed = getMostDetailedQuote();

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <Link
              to={`/projects/${id}/tasks/${taskId}/quotes`}
              className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300 backdrop-blur-sm"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Quotes
            </Link>
          </div>
          
          <div className="flex items-start">
            <ScaleIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Quote Comparison</h1>
              <p className="text-pink-100 text-xl">
                {task?.name} - {formatProjectName(project)}
              </p>
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2" />
                  {quotes.length} Quotes to Compare
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        {quotes.length === 0 ? (
          <div className="text-center py-12">
            <ScaleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No quotes to compare</h3>
            <p className="text-gray-500 mb-6">There are no submitted quotes available for comparison</p>
            <Link
              to={`/projects/${id}/tasks/${taskId}/quotes`}
              className="inline-flex items-center px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Quotes
            </Link>
          </div>
        ) : (
          <>
            {/* Recommendations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6 pfi-card">
                <div className="flex items-center mb-4">
                  <TrophyIcon className="w-8 h-8 text-green-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">Best Value</h3>
                </div>
                {bestValue && (
                  <div>
                    <div className="text-xl font-bold text-green-600 mb-2">{bestValue.supplierName}</div>
                    <div className="text-2xl font-bold text-gray-900 mb-2">{formatCurrency(bestValue.amount)}</div>
                    <div className="text-sm text-gray-600">
                      Score: {calculateScore(bestValue)}/100 • {formatTimeline(bestValue)}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6 pfi-card">
                <div className="flex items-center mb-4">
                  <CurrencyDollarIcon className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">Lowest Price</h3>
                </div>
                {lowestPrice && (
                  <div>
                    <div className="text-xl font-bold text-blue-600 mb-2">{lowestPrice.supplierName}</div>
                    <div className="text-2xl font-bold text-gray-900 mb-2">{formatCurrency(lowestPrice.amount)}</div>
                    <div className="text-sm text-gray-600">
                      Timeline: {formatTimeline(lowestPrice)}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6 pfi-card">
                <div className="flex items-center mb-4">
                  <ClockIcon className="w-8 h-8 text-orange-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">Most Detailed</h3>
                </div>
                {mostDetailed && (
                  <div>
                    <div className="text-xl font-bold text-orange-600 mb-2">{mostDetailed.supplierName}</div>
                    <div className="text-2xl font-bold text-gray-900 mb-2">{formatTimeline(mostDetailed)}</div>
                    <div className="text-sm text-gray-600">
                      {formatCurrency(mostDetailed.amount)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-white rounded-2xl shadow-xl border border-pink-100 overflow-hidden">
              <div className="px-8 py-6 bg-gradient-to-r from-pink-50 to-orange-50 border-b">
                <h2 className="text-2xl font-bold text-gray-900">Detailed Comparison</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Supplier</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Quote Amount</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Completion</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Performance</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Score</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {quotes
                      .sort((a, b) => calculateScore(b) - calculateScore(a))
                      .map((quote) => {
                        const score = calculateScore(quote);
                        const isBestValue = bestValue && quote.id === bestValue.id;
                        const isLowestPrice = lowestPrice && quote.id === lowestPrice.id;
                        const isMostDetailed = mostDetailed && quote.id === mostDetailed.id;
                        
                        return (
                          <React.Fragment key={quote.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div>
                                  <div className="font-semibold text-gray-900 flex items-center">
                                    {quote.supplierName}
                                    {isBestValue && <TrophyIcon className="w-4 h-4 text-green-500 ml-2" title="Best Value" />}
                                    {isLowestPrice && <CurrencyDollarIcon className="w-4 h-4 text-blue-500 ml-2" title="Lowest Price" />}
                                    {isMostDetailed && <ClockIcon className="w-4 h-4 text-orange-500 ml-2" title="Most Detailed" />}
                                  </div>
                                  <div className="text-sm text-gray-600">{quote.supplierEmail}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-lg font-bold text-gray-900">{formatCurrency(quote.amount)}</div>
                                {quote.notes && (
                                  <div className="text-xs text-gray-500 mt-1">{quote.notes.substring(0, 50)}...</div>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-semibold text-gray-900">{formatTimeline(quote)}</div>
                                {quote.notes && (
                                  <div className="text-xs text-gray-500 mt-1">{quote.notes.substring(0, 30)}...</div>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {(() => {
                                  const performance = supplierPerformance[quote.supplierId];
                                  if (performance && performance.hasHistory) {
                                    const badge = getPerformanceBadge(performance.averageRating);
                                    return (
                                      <div>
                                        <div className="flex items-center space-x-2">
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bgColor} ${badge.textColor}`}>
                                            ★ {performance.averageRating.toFixed(1)}
                                          </span>
                                          {performance.similarWork?.hasSimilarWork && (
                                            <span className="text-xs text-green-600 font-medium">Similar Work</span>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {performance.projectCount} projects • {performance.recommendationRate}% recommend
                                        </div>
                                        {performance.recentTrend === 'improving' && (
                                          <div className="text-xs text-green-600">↗ Improving</div>
                                        )}
                                        {performance.recentTrend === 'declining' && (
                                          <div className="text-xs text-red-600">↘ Declining</div>
                                        )}
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div className="text-xs text-gray-500">
                                        <div>New Supplier</div>
                                        <div>No history</div>
                                      </div>
                                    );
                                  }
                                })()}
                              </td>
                              <td className="px-6 py-4">
                                <div className="space-y-2">
                                  <div className="flex items-center">
                                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                                      <div 
                                        className="bg-gradient-to-r from-pink-400 to-orange-400 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${score}%` }}
                                      ></div>
                                    </div>
                                    <span className="font-semibold text-gray-900">{score}</span>
                                  </div>
                                  {/* Score breakdown */}
                                  {(() => {
                                    const aiAnalysis = aiAnalyses[quote.id];
                                    const aiScore = aiAnalysis?.qualityScore || 50;
                                    const aiPoints = Math.round((aiScore / 100) * 25);
                                    
                                    // Calculate other score components for transparency
                                    const prices = quotes.map(q => q.amount);
                                    const minPrice = Math.min(...prices);
                                    const maxPrice = Math.max(...prices);
                                    const pricePoints = maxPrice > minPrice 
                                      ? Math.round(((maxPrice - quote.amount) / (maxPrice - minPrice)) * 30)
                                      : 30;
                                    
                                    let timelinePoints = 0;
                                    if (quote.timelineDays) {
                                      const taskCategory = task?.category?.toLowerCase();
                                      if (taskCategory === 'upholstery') {
                                        if (quote.timelineDays <= 30) timelinePoints = 15;
                                        else if (quote.timelineDays <= 60) timelinePoints = 13;
                                        else if (quote.timelineDays <= 90) timelinePoints = 11;
                                        else if (quote.timelineDays <= 120) timelinePoints = 9;
                                        else if (quote.timelineDays <= 150) timelinePoints = 6;
                                        else if (quote.timelineDays <= 180) timelinePoints = 4;
                                        else timelinePoints = 2;
                                      } else {
                                        if (quote.timelineDays <= 7) timelinePoints = 15;
                                        else if (quote.timelineDays <= 14) timelinePoints = 13;
                                        else if (quote.timelineDays <= 21) timelinePoints = 11;
                                        else if (quote.timelineDays <= 30) timelinePoints = 9;
                                        else if (quote.timelineDays <= 45) timelinePoints = 6;
                                        else timelinePoints = 4;
                                      }
                                    } else if (quote.timeline) {
                                      timelinePoints = 7;
                                    }
                                    
                                    return (
                                      <div className="text-xs text-gray-500 space-y-1">
                                        <div className="flex justify-between">
                                          <span>AI Quality:</span>
                                          <span className="font-medium">{aiPoints}/25</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Price:</span>
                                          <span>{pricePoints}/30</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Timeline:</span>
                                          <span>{timelinePoints}/15</span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <button
                                  onClick={() => handleAcceptQuote(quote.id)}
                                  disabled={acceptingQuote === quote.id}
                                  className={`inline-flex items-center px-4 py-2 font-semibold rounded-lg transition-colors ${
                                    acceptingQuote === quote.id
                                      ? 'bg-green-400 text-white cursor-not-allowed'
                                      : 'bg-green-600 text-white hover:bg-green-700'
                                  }`}
                                >
                                  {acceptingQuote === quote.id ? (
                                    <>
                                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                                      Accept
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                            {/* AI Analysis Row */}
                            <tr>
                              <td colSpan="6" className="px-6 py-4 bg-gradient-to-br from-purple-50 to-blue-50 border-b border-purple-100">
                                {loadingAnalysis[quote.id] ? (
                                  <div className="flex items-center justify-center space-x-3 py-4">
                                    <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                    <span className="text-purple-600 font-medium">AI analyzing quote and supplier history...</span>
                                  </div>
                                ) : aiAnalyses[quote.id] ? (
                                  <div>
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center">
                                        <ChartBarIcon className="w-5 h-5 text-purple-600 mr-2" />
                                        <h4 className="text-sm font-semibold text-purple-700">AI Analysis</h4>
                                        <span className="ml-2 text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                                          ✨ AI-powered insights
                                        </span>
                                      </div>
                                      {aiAnalyses[quote.id].qualityScore && (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-sm font-medium text-purple-700">AI Quality Score:</span>
                                          <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                                            aiAnalyses[quote.id].qualityScore >= 80 ? 'bg-green-100 text-green-800' :
                                            aiAnalyses[quote.id].qualityScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                            {aiAnalyses[quote.id].qualityScore}/100
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
                                      {aiAnalyses[quote.id].analysis || 'Analysis pending...'}
                                    </div>
                                    {aiAnalyses[quote.id].keyFlags && aiAnalyses[quote.id].keyFlags.length > 0 && (
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {aiAnalyses[quote.id].keyFlags.map((flag, index) => (
                                          <span 
                                            key={index}
                                            className={`px-2 py-1 text-xs rounded-full ${
                                              flag.toLowerCase().includes('concern') || flag.toLowerCase().includes('risk') || flag.toLowerCase().includes('poor')
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-green-100 text-green-700'
                                            }`}
                                          >
                                            {flag}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center py-4">
                                    <div className="text-sm text-gray-500 italic">
                                      AI analysis unavailable for this quote
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Scoring Explanation */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <div className="flex items-start">
                <StarIcon className="w-6 h-6 text-blue-500 mr-3 mt-1" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-2">How Scores Are Calculated (Max 100 Points)</p>
                  <div className="space-y-1">
                    <p>• <strong>Price (30 pts)</strong>: Lower prices receive higher scores</p>
                    <p>• <strong>Timeline (15 pts)</strong>: Category-specific scales:</p>
                    <div className="ml-4 text-xs">
                      <p><em>Upholstery:</em> 1 month (15pts) → 6 months (4pts)</p>
                      <p><em>Lighting/Other:</em> 1 week (15pts) → 6 weeks (4pts)</p>
                    </div>
                    <p>• <strong>AI Quality Assessment (25 pts)</strong>: <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">✨ NEW</span> Quote professionalism, material quality, and reliability indicators</p>
                    <p>• <strong>Historical Performance (20 pts)</strong>: Past ratings and project success</p>
                    <p>• <strong>Quote Completeness (10 pts)</strong>: Description length and specific timeline provided</p>
                    <p className="text-xs text-gray-500 mt-2">
                      • Similar work experience: +2 pts • Improving trend: +1.5 pts • Declining trend: -1.5 pts
                    </p>
                    <p className="text-xs bg-purple-50 text-purple-700 mt-2 p-2 rounded">
                      <strong>AI Quality Assessment</strong> analyzes quote descriptions to identify materials quality, professional craftsmanship promises, and potential risks - preventing poor quality quotes from ranking highly based solely on low price.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}